/**
 * Taking the agent offline while the widget window is closing.
 *
 * An XHR started in `beforeunload` dies with the page, so axios cannot be used on that path. `fetch`
 * with `keepalive: true` is the one request the browser promises to finish after the document is
 * gone. `navigator.sendBeacon` would be the other candidate and is rejected here: it cannot set an
 * Authorization header, and this endpoint is authenticated.
 *
 * Deliberately fire-and-forget. There is no page left to report a failure to, and the agent is also
 * taken offline on every non-unload path, so this is the last of several chances rather than the only
 * one.
 *
 * No campaign id: `go-offline` pauses every softphone campaign ringing the agent server-side, so the
 * request is right even if this window's idea of the live campaign was stale.
 */
export function goOfflineWithKeepalive(accountId, accessToken) {
  if (!accountId || !accessToken || typeof fetch === 'undefined') {
    return false
  }

  // The trailing slash is stripped because this is the ONE request in the app that builds its URL
  // by hand - every other call goes through axios, which collapses `baseURL` + path into a single
  // slash. `VUE_APP_BASE_URL` ends with one in staging and production (`.../api/v1/`) and does not
  // in development, so concatenating raw produced `/api/v1//customerresources/...`: an empty path
  // segment the API answers with 404. Fire-and-forget, so nothing surfaced it - the agent's
  // campaigns simply stayed live after a tab close or refresh, in staging and production only.
  const baseUrl = (process.env.VUE_APP_BASE_URL || '').replace(/\/+$/, '')

  const url = `${baseUrl}/customerresources/${accountId}/softphone/campaigns/go-offline`

  try {
    fetch(url, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    }).catch(() => {
      // Nothing to recover to - the window is going away.
    })

    return true
  } catch (error) {
    return false
  }
}

export default goOfflineWithKeepalive
